"use client";
import React, { useEffect, useState, useRef, useCallback } from "react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRemoval = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onRemove(toast.id), 300); // Wait for animation
      timerRef.current = null;
    }, 5000);
  }, [toast.id, onRemove]);

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    scheduleRemoval();
  };

  useEffect(() => {
    scheduleRemoval();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [toast.id, scheduleRemoval]);

  const bgColor = {
    success: "bg-yellow-50 border border-yellow-200",
    error: "bg-red-500",
    info: "bg-blue-500",
    warning: "bg-yellow-500",
  }[toast.type];

  const textColor = {
    success: "text-yellow-900",
    error: "text-white",
    info: "text-white",
    warning: "text-white",
  }[toast.type];

  const closeButtonColor = {
    success: "text-yellow-700 hover:text-yellow-900 hover:bg-yellow-100",
    error: "text-white hover:text-gray-200",
    info: "text-white hover:text-gray-200",
    warning: "text-white hover:text-gray-200",
  }[toast.type];

  return (
    <div
      className={`${bgColor} ${textColor} px-6 py-3 rounded-lg shadow-lg mb-2 transition-all duration-300 ${
        isExiting ? "opacity-0 -translate-y-2" : "opacity-100 translate-y-0"
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center justify-between">
        <span className="flex-1">{toast.message}</span>
        <button
          onClick={() => {
            setIsExiting(true);
            setTimeout(() => onRemove(toast.id), 300);
          }}
          className={`ml-4 flex-shrink-0 p-1 rounded transition-colors ${closeButtonColor}`}
          title="关闭"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 z-[100] w-full max-w-md -translate-x-1/2 transform px-4">
      <div className="space-y-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </div>
    </div>
  );
};

